import {
  createAction,
  createSlice,
  PayloadAction
} from '@reduxjs/toolkit'
import type { RootState } from "@/store/types"
import {
  removeAccessTokenFromApp,
  sendAccessTokenToApp,
  sendTokenToApp
} from '@/utils/app'
import { localStorageFields } from '@/constants/localStorage'
import { apiRequest } from '@/api/apiRequestInit'
import type { IInitUser } from './interfaces'

const FIELD_NAME = localStorageFields.currentUser

export const changeToken = (token?: string | null) => {
  if (token) {
    apiRequest.setToken('Bearer ' + token)
    sendTokenToApp(token)
  } else {
    apiRequest.setToken(undefined)
  }
}

const DELETED_FIELDS: (keyof IInitUser)[] = ['token']

const saveUser = (user: IInitUser | null) => {
  if (!user) {
    localStorage.setItem(FIELD_NAME, JSON.stringify(user))
    return
  }
  const savedUser = { ...user }
  DELETED_FIELDS.forEach(field => {
    delete savedUser[field]
  })
  localStorage.setItem(FIELD_NAME, JSON.stringify(savedUser))
}

const initUser = JSON.parse('' + localStorage.getItem(FIELD_NAME)) as (IInitUser | null)
changeToken(initUser?.token || '')

if (initUser) {
  sendAccessTokenToApp({
    accessToken: initUser.accessToken,
    memberId: initUser.memberId
  })
}

export const user = createSlice({
  name: 'user',
  initialState: {
    user: initUser
  },
  reducers: {
    setUser: (state, { payload }: PayloadAction<IInitUser | null>) => {
      if (payload &&
        (state.user?.token !== payload.token ||
          state.user?.memberId !== payload.memberId)
      ) {
        sendAccessTokenToApp({
          accessToken: payload.accessToken,
          memberId: payload.memberId
        })
      } else if (!payload?.token) {
        removeAccessTokenFromApp()
      }
      state.user = payload
      changeToken(payload?.token)
      if (state.user && !state.user.$jid) {
        state.user.$jid = state.user.memberId + '@' + import.meta.env.VITE_EJ_HOST
      }
      saveUser(payload)
    },
    setAdditionalFields(state, { payload }: PayloadAction<Partial<IInitUser>>) {
      if (!state.user) {
        return
      }
      // An authenticate request started for a previous session can resolve after
      // a new one (SSO re-login) has already been established — its fields would
      // overwrite the fresh token.
      if (payload.memberId && payload.memberId !== state.user.memberId) {
        return
      }
      if (payload.token) {
        changeToken(payload.token)
      }
      state.user = {
        ...state.user,
        ...payload
      }
      saveUser(state.user)
    }
  }
})

export const { setUser, setAdditionalFields } = user.actions

// Signal action: triggers the auth listener to establish a session at app
// startup (when the user is restored from localStorage, no setUser is fired).
export const sessionRequested = createAction('user/sessionRequested')

export const getUser = (state: RootState): IInitUser | null => state.user.user

// Direct registration (true or undefined) hides MyTeam/Service tabs and News/Alogix chats;
// they are shown only to non-direct (sponsored) users — directRegistration === false.
export const getIsDirectRegistration = (state: RootState): boolean =>
  state.user.user?.directRegistration !== false

export default user.reducer
